import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { lcApiBase, readObservableError } from './logiciel-caisse-api.helper';
import { LogicielCaisseAuthService } from './logiciel-caisse-auth.service';

export interface LcBonReceptionEntreeLigne {
  code_barre?: string | null;
  ref_interne?: string | null;
  ref_fabricant?: string | null;
  qnt: number;
  libelle?: string | null;
  /** Id produit LC / ERP (repli si code-barres absent). */
  product_id?: number | null;
  produits_id?: number | null;
}

export type LcStockMouvementType = 'entree' | 'sortie';

/**
 * Stock logiciel-caisse identifié par code-barres (pas product_id ERP).
 */
@Injectable({ providedIn: 'root' })
export class LogicielCaisseStockReceptionService {
  constructor(
    private readonly http: HttpClient,
    private readonly authService: LogicielCaisseAuthService
  ) {}

  /**
   * POST /api/stocks/bon-reception-entree
   * Entrée stock par code-barres (idempotente via reference_document).
   */
  entreeByBarcode(params: {
    entrepot_id: number;
    entrepot_initial_id?: number;
    entrepot_destination_id?: number;
    from_pv_ecart_regularisation?: boolean;
    reference_document?: string | null;
    lignes: Array<
      LcBonReceptionEntreeLigne & { sens?: 'MANQUANT' | 'EXCEDENT' | string }
    >;
  }): Observable<{ success: true; updated: number; already_applied?: boolean }> {
    return this.ensureSession().pipe(
      switchMap(() => {
        const entrepotId = Number(params.entrepot_id);
        if (!Number.isFinite(entrepotId) || entrepotId <= 0) {
          return throwError(() => new Error('Entrepôt logiciel caisse invalide.'));
        }
        const lignes = (params.lignes || [])
          .map((l) => {
            const row: Record<string, unknown> = {
              code_barre: this.cleanBarcode(l.code_barre),
              ref_interne: this.cleanRef(l.ref_interne),
              qnt: Math.abs(Number(l.qnt) || 0),
              libelle: l.libelle != null ? String(l.libelle).trim() || null : null,
            };
            const sens = String((l as { sens?: string }).sens ?? '').trim();
            if (sens) {
              row.sens = sens;
            }
            return row;
          })
          .filter((l) => Number(l.qnt) > 0 && (l.code_barre || l.ref_interne));

        if (!lignes.length) {
          return throwError(
            () =>
              new Error(
                'Aucune ligne avec code-barres / référence. Ajoutez le code-barres sur chaque produit.'
              )
          );
        }

        const url = `${lcApiBase(this.authService)}/api/stocks/bon-reception-entree`;
        const ids = this.authService.readLcIdsFromLocalStorage();
        const body: Record<string, unknown> = {
          entrepot_id: entrepotId,
          reference_document: params.reference_document || null,
          lignes,
        };
        if (ids) {
          body.user_id = ids.userId;
          body.station_id = ids.stationId;
        }
        if (params.from_pv_ecart_regularisation) {
          body.from_pv_ecart_regularisation = true;
          const init = Number(params.entrepot_initial_id);
          const dest = Number(params.entrepot_destination_id);
          if (Number.isFinite(init) && init > 0) {
            body.entrepot_initial_id = init;
          }
          if (Number.isFinite(dest) && dest > 0) {
            body.entrepot_destination_id = dest;
          }
        }
        return this.http
          .post<{
            success?: boolean;
            updated?: number;
            already_applied?: boolean;
            error?: string;
            message?: string;
          }>(
            url,
            body,
            { headers: this.authHeaders() }
          )
          .pipe(
            map((res) => {
              if (!res?.success) {
                throw new Error(res?.error || res?.message || 'Entrée stock caisse refusée');
              }
              return {
                success: true as const,
                updated: Number(res.updated) || 0,
                already_applied: !!res.already_applied,
              };
            }),
            catchError((err: unknown) => {
              if (err instanceof Error && !(err instanceof HttpErrorResponse)) {
                return throwError(() => err);
              }
              return throwError(
                () => new Error(readObservableError(err, 'Échec entrée stock par code-barres.'))
              );
            })
          );
      })
    );
  }

  /**
   * POST /api/stocks/bon-sortie-sortie
   * Sortie stock_physique par code-barres (idempotente via reference_document).
   */
  sortieByBarcode(params: {
    entrepot_id: number;
    entrepot_dest_id?: number;
    reference_document?: string | null;
    /** Bon de sortie régularisation PV d'écart : physique + libération réservation transfert. */
    from_pv_ecart_regularisation?: boolean;
    reservation_reference_document?: string | null;
    lignes: Array<
      LcBonReceptionEntreeLigne & { sens?: 'MANQUANT' | 'EXCEDENT' | string }
    >;
  }): Observable<{
    success: true;
    updated: number;
    already_applied?: boolean;
    skipped?: unknown[];
    results?: unknown[];
  }> {
    return this.ensureSession().pipe(
      switchMap(() => {
        const entrepotId = Number(params.entrepot_id);
        if (!Number.isFinite(entrepotId) || entrepotId <= 0) {
          return throwError(() => new Error('Entrepôt logiciel caisse invalide.'));
        }
        const lignes = (params.lignes || [])
          .map((l) => {
            const pidRaw = l.produits_id ?? l.product_id;
            const pid = Number(pidRaw);
            const row: Record<string, unknown> = {
              code_barre: this.cleanBarcode(l.code_barre),
              ref_interne: this.cleanRef(l.ref_interne),
              qnt: Math.abs(Number(l.qnt) || 0),
              libelle: l.libelle != null ? String(l.libelle).trim() || null : null,
              product_id: Number.isFinite(pid) && pid > 0 ? pid : undefined,
            };
            const sens = String((l as { sens?: string }).sens ?? '').trim();
            if (sens) {
              row.sens = sens;
            }
            return row;
          })
          .filter(
            (l) =>
              Number(l.qnt) > 0 &&
              !!(l.code_barre || l.ref_interne || l.product_id)
          );

        if (!lignes.length) {
          return throwError(
            () =>
              new Error(
                'Aucune ligne exploitable (code-barres, référence ou id produit).'
              )
          );
        }

        const url = `${lcApiBase(this.authService)}/api/stocks/bon-sortie-sortie`;
        const payload: Record<string, unknown> = {
          entrepot_id: entrepotId,
          reference_document: params.reference_document || null,
          lignes,
        };
        if (params.from_pv_ecart_regularisation) {
          payload.from_pv_ecart_regularisation = true;
          const dest = Number(params.entrepot_dest_id);
          if (Number.isFinite(dest) && dest > 0) {
            payload.entrepot_dest_id = dest;
          }
          const resRef = String(params.reservation_reference_document ?? '').trim();
          if (resRef) {
            payload.reservation_reference_document = resRef;
          }
        }
        console.log('[BonSortieStock] POST bon-sortie-sortie', payload);

        return this.http
          .post<{
            success?: boolean;
            updated?: number;
            already_applied?: boolean;
            error?: string;
            message?: string;
            skipped?: unknown[];
            results?: unknown[];
          }>(url, payload, { headers: this.authHeaders() })
          .pipe(
            map((res) => {
              console.log('[BonSortieStock] réponse API', res);
              if (!res?.success) {
                throw new Error(res?.error || res?.message || 'Sortie stock caisse refusée');
              }
              return {
                success: true as const,
                updated: Number(res.updated) || 0,
                already_applied: !!res.already_applied,
                skipped: res.skipped,
                results: res.results,
              };
            }),
            catchError((err: unknown) => {
              if (err instanceof HttpErrorResponse) {
                const body = err.error as Record<string, unknown> | null;
                console.error('[BonSortieStock] erreur HTTP', {
                  status: err.status,
                  body,
                });
                let msg = readObservableError(err, 'Échec sortie stock bon de sortie.');
                const skipped = body?.skipped;
                if (Array.isArray(skipped) && skipped.length > 0) {
                  const parts = skipped
                    .map((s: any) => s?.reason || s?.error || JSON.stringify(s))
                    .filter(Boolean);
                  if (parts.length) {
                    msg = `${msg} (${parts.join(' ; ')})`;
                  }
                }
                return throwError(() => new Error(msg));
              }
              if (err instanceof Error) {
                return throwError(() => err);
              }
              return throwError(
                () => new Error('Échec sortie stock bon de sortie.')
              );
            })
          );
      })
    );
  }

  /**
   * POST /api/stocks/bon-transfert-sortie
   * Sortie stock_disponible à l'entrepôt départ (idempotente via reference_document).
   */
  bonTransfertSortieByBarcode(params: {
    entrepot_id: number;
    entrepot_dest_id: number;
    reference_document?: string | null;
    /** Bon de transfert créé depuis un PV d'écart : quantités signées ( − = entrée destination, + = sortie départ ). */
    from_pv_ecart?: boolean;
    /** Avec from_pv_ecart : both = stock_disponible + stock_physique (défaut). */
    stock_mode?: 'both' | 'disponible_only';
    lignes: LcBonReceptionEntreeLigne[];
  }): Observable<{
    success: true;
    updated: number;
    already_applied?: boolean;
    skipped?: unknown[];
    results?: unknown[];
  }> {
    return this.ensureSession().pipe(
      switchMap(() => {
        const entrepotId = Number(params.entrepot_id);
        const entrepotDestId = Number(params.entrepot_dest_id);
        if (!Number.isFinite(entrepotId) || entrepotId <= 0) {
          return throwError(() => new Error('Entrepôt départ logiciel caisse invalide.'));
        }
        if (!Number.isFinite(entrepotDestId) || entrepotDestId <= 0) {
          return throwError(() => new Error('Entrepôt destination logiciel caisse invalide.'));
        }
        if (entrepotId === entrepotDestId) {
          return throwError(
            () => new Error('Les entrepôts départ et destination doivent être différents.')
          );
        }
        const fromPvEcart = !!params.from_pv_ecart;
        const lignes = (params.lignes || [])
          .map((l) => {
            const rawQ = Number(l.qnt) || 0;
            return {
              code_barre: this.cleanBarcode(l.code_barre),
              ref_interne: this.cleanRef(l.ref_interne),
              ref_fabricant: this.cleanRef(l.ref_fabricant),
              qnt: fromPvEcart ? rawQ : Math.abs(rawQ),
              libelle: l.libelle != null ? String(l.libelle).trim() || null : null,
            };
          })
          .filter(
            (l) =>
              (fromPvEcart ? l.qnt !== 0 : l.qnt > 0) &&
              !!(l.code_barre || l.ref_interne || l.ref_fabricant)
          );

        if (!lignes.length) {
          return throwError(
            () =>
              new Error(
                'Aucune ligne exploitable (code-barres ou référence produit logiciel caisse).'
              )
          );
        }

        const url = `${lcApiBase(this.authService)}/api/stocks/bon-transfert-sortie`;
        const payload: Record<string, unknown> = {
          entrepot_id: entrepotId,
          entrepot_dest_id: entrepotDestId,
          reference_document: params.reference_document || null,
          lignes,
        };
        if (fromPvEcart) {
          payload.from_pv_ecart = true;
          payload.stock_mode = params.stock_mode === 'disponible_only' ? 'disponible_only' : 'both';
        }
        console.log('[BonTransfertStock] POST bon-transfert-sortie', payload);

        return this.http
          .post<{
            success?: boolean;
            updated?: number;
            already_applied?: boolean;
            error?: string;
            message?: string;
            skipped?: unknown[];
            results?: unknown[];
          }>(url, payload, { headers: this.authHeaders() })
          .pipe(
            map((res) => {
              if (!res?.success) {
                throw new Error(res?.error || res?.message || 'Sortie stock caisse refusée');
              }
              return {
                success: true as const,
                updated: Number(res.updated) || 0,
                already_applied: !!res.already_applied,
                skipped: res.skipped,
                results: res.results,
              };
            }),
            catchError((err: unknown) => {
              if (err instanceof HttpErrorResponse) {
                const body = err.error as Record<string, unknown> | null;
                let msg = readObservableError(err, 'Échec sortie stock bon de transfert.');
                const skipped = body?.skipped;
                if (Array.isArray(skipped) && skipped.length > 0) {
                  const parts = skipped
                    .map((s: any) => s?.reason || s?.error || JSON.stringify(s))
                    .filter(Boolean);
                  if (parts.length) {
                    msg = `${msg} (${parts.join(' ; ')})`;
                  }
                }
                return throwError(() => new Error(msg));
              }
              if (err instanceof Error) {
                return throwError(() => err);
              }
              return throwError(
                () => new Error('Échec sortie stock bon de transfert.')
              );
            })
          );
      })
    );
  }

  /**
   * PUT /api/stocks/by-code-barre — délègue à bon-reception-entree pour les entrées.
   */
  mouvementByBarcode(params: {
    entrepot_id: number;
    type_mouvement: LcStockMouvementType;
    motif?: string | null;
    reference_document?: string | null;
    lignes: LcBonReceptionEntreeLigne[];
  }): Observable<{ success: true; updated: number; already_applied?: boolean }> {
    if (params.type_mouvement === 'sortie') {
      return throwError(
        () => new Error('Sortie stock par code-barres non disponible sur cet endpoint.')
      );
    }
    return this.entreeByBarcode({
      entrepot_id: params.entrepot_id,
      reference_document: params.reference_document,
      lignes: params.lignes,
    });
  }

  /**
   * POST /api/stocks/pv-ecart-regularisation
   * Manquant / excédent PV d'écart (stock physique + disponible, idempotent).
   */
  pvEcartRegularisation(params: {
    entrepot_initial_id: number;
    entrepot_destination_id: number;
    reference_document?: string | null;
    pv_ecart_id?: number | null;
    lignes: Array<{
      sens: 'MANQUANT' | 'EXCEDENT';
      qnt: number;
      code_barre?: string | null;
      ref_interne?: string | null;
      ref_fabricant?: string | null;
    }>;
  }): Observable<{
    success: true;
    updated: number;
    already_applied?: boolean;
    skipped?: unknown[];
    results?: unknown[];
  }> {
    return this.ensureSession().pipe(
      switchMap(() => {
        const entrepotInitialId = Number(params.entrepot_initial_id);
        const entrepotDestId = Number(params.entrepot_destination_id);
        if (!Number.isFinite(entrepotInitialId) || entrepotInitialId <= 0) {
          return throwError(() => new Error('Entrepôt initial logiciel caisse invalide.'));
        }
        if (!Number.isFinite(entrepotDestId) || entrepotDestId <= 0) {
          return throwError(() => new Error('Entrepôt destination logiciel caisse invalide.'));
        }
        if (entrepotInitialId === entrepotDestId) {
          return throwError(
            () => new Error('Les entrepôts initial et destination doivent être différents.')
          );
        }
        const lignes = (params.lignes || [])
          .map((l) => ({
            sens: l.sens === 'EXCEDENT' ? 'EXCEDENT' : 'MANQUANT',
            qnt: Math.abs(Number(l.qnt) || 0),
            code_barre: this.cleanBarcode(l.code_barre),
            ref_interne: this.cleanRef(l.ref_interne),
            ref_fabricant: this.cleanRef(l.ref_fabricant),
          }))
          .filter(
            (l) =>
              l.qnt > 0 && (l.code_barre || l.ref_interne || l.ref_fabricant)
          );
        if (!lignes.length) {
          return throwError(
            () =>
              new Error(
                'Aucune ligne exploitable (code-barres ou référence requis sur chaque produit).'
              )
          );
        }
        const url = `${lcApiBase(this.authService)}/api/stocks/pv-ecart-regularisation`;
        return this.http
          .post<{
            success?: boolean;
            updated?: number;
            already_applied?: boolean;
            error?: string;
            message?: string;
          }>(
            url,
            {
              entrepot_initial_id: entrepotInitialId,
              entrepot_destination_id: entrepotDestId,
              reference_document: params.reference_document || null,
              pv_ecart_id: params.pv_ecart_id ?? null,
              lignes,
            },
            { headers: this.authHeaders() }
          )
          .pipe(
            map((res) => {
              if (res?.success === false) {
                throw new Error(res.error || res.message || 'Échec régularisation stock PV écart.');
              }
              return {
                success: true as const,
                updated: Number(res?.updated) || 0,
                already_applied: !!res?.already_applied,
              };
            }),
            catchError((err: HttpErrorResponse | Error) => {
              if (err instanceof HttpErrorResponse && err.status === 404) {
                const notFound = new Error(
                  'Route régularisation PV écart absente sur le serveur logiciel caisse (404).'
                ) as Error & { status?: number };
                notFound.status = 404;
                return throwError(() => notFound);
              }
              const msg = readObservableError(err, 'Échec régularisation stock PV écart.');
              return throwError(() => new Error(msg));
            })
          );
      })
    );
  }

  /**
   * PUT /api/stocks/pv-ecart-transfert-approuve-disponible
   * Mise à jour stock_disponible (delta écart signé) — fiche stock existante uniquement.
   */
  pvEcartTransfertApprouveDisponible(params: {
    entrepot_id_depart: number;
    reference_document: string;
    transfert_id?: number;
    detail_lignes?: Array<Record<string, unknown>>;
    lignes: Array<{
      quantite: number;
      code_barre?: string | null;
      ref_interne?: string | null;
      ref_fabricant?: string | null;
      libelle?: string | null;
      product_id?: number | null;
    }>;
  }): Observable<{
    success: true;
    updated: number;
    already_applied?: boolean;
  }> {
    return this.ensureSession().pipe(
      switchMap(() => {
        const entrepotDepart = Number(params.entrepot_id_depart);
        const ref = String(params.reference_document ?? '').trim();
        if (!Number.isFinite(entrepotDepart) || entrepotDepart <= 0) {
          return throwError(() => new Error('Entrepôt départ logiciel caisse invalide.'));
        }
        if (!ref) {
          return throwError(
            () => new Error('Référence transfert (num_document LC) requise.')
          );
        }
        const lignes = (params.lignes || [])
          .map((l) => ({
            quantite: Math.trunc(Number(l.quantite) || 0),
            code_barre: this.cleanBarcode(l.code_barre),
            ref_interne: this.cleanRef(l.ref_interne),
            ref_fabricant: this.cleanRef(l.ref_fabricant),
            libelle: l.libelle != null ? String(l.libelle).trim() || null : null,
            product_id:
              l.product_id != null && Number(l.product_id) > 0
                ? Number(l.product_id)
                : null,
          }))
          .filter(
            (l) =>
              l.quantite !== 0 &&
              !!(l.code_barre || l.ref_interne || l.ref_fabricant || l.product_id)
          );
        if (!lignes.length) {
          return throwError(
            () =>
              new Error(
                'Aucune ligne signée exploitable pour la correction stock disponible.'
              )
          );
        }
        const url = `${lcApiBase(this.authService)}/api/stocks/pv-ecart-transfert-approuve-disponible`;
        const transfertId = Number(params.transfert_id);
        const body: Record<string, unknown> = {
          entrepot_id_depart: entrepotDepart,
          reference_document: ref,
          lignes,
        };
        if (Number.isFinite(transfertId) && transfertId > 0) {
          body.transfert_id = transfertId;
        }
        if (params.detail_lignes?.length) {
          body.detail_lignes = params.detail_lignes;
        }
        return this.http
          .put<{
            success?: boolean;
            updated?: number;
            lignes_synced?: number;
            already_applied?: boolean;
            error?: string;
            message?: string;
          }>(
            url,
            body,
            { headers: this.authHeaders() }
          )
          .pipe(
            map((res) => {
              if (res?.success === false) {
                throw new Error(
                  res.error || res.message || 'Échec correction stock disponible PV écart.'
                );
              }
              return {
                success: true as const,
                updated: Number(res?.updated) || 0,
                already_applied: !!res?.already_applied,
              };
            }),
            catchError((err: HttpErrorResponse | Error) => {
              const msg = readObservableError(
                err,
                'Échec correction stock disponible PV écart.'
              );
              return throwError(() => new Error(msg));
            })
          );
      })
    );
  }

  /** @deprecated Utiliser entreeByBarcode / mouvementByBarcode */
  createReception(body: {
    entrepot_id: number;
    document_libelle?: string | null;
    details: Array<{
      produits_id?: number | null;
      qnt: number;
      libelle?: string | null;
      ref_interne?: string | null;
      ref_externe?: string | null;
      code_barre?: string | null;
    }>;
  }): Observable<{ success: true; reception: unknown }> {
    return this.entreeByBarcode({
      entrepot_id: body.entrepot_id,
      reference_document: body.document_libelle,
      lignes: (body.details || []).map((d) => ({
        code_barre: d.code_barre,
        ref_interne: d.ref_interne || d.ref_externe,
        qnt: d.qnt,
        libelle: d.libelle,
      })),
    }).pipe(map((r) => ({ success: true as const, reception: r })));
  }

  /**
   * GET /api/stocks/by-code-barre?code_barre=&entrepot_id=
   */
  getStockByBarcode(
    codeBarre: string,
    entrepotId?: number | null
  ): Observable<number> {
    const cb = this.cleanBarcode(codeBarre);
    if (!cb) {
      return of(0);
    }
    return this.ensureSession().pipe(
      switchMap(() => {
        let params = new HttpParams().set('code_barre', cb);
        const eid = Number(entrepotId);
        if (Number.isFinite(eid) && eid > 0) {
          params = params.set('entrepot_id', String(eid));
        }
        const url = `${lcApiBase(this.authService)}/api/stocks/by-code-barre`;
        return this.http
          .get<{ success?: boolean; data?: { total_quantity?: number; stock_physique?: number } }>(
            url,
            { headers: this.authHeaders(), params }
          )
          .pipe(
            map((res) => {
              const n = Number(res?.data?.total_quantity ?? res?.data?.stock_physique ?? 0);
              return Number.isFinite(n) ? n : 0;
            }),
            catchError(() => of(0))
          );
      }),
      catchError(() => of(0))
    );
  }

  /**
   * POST /api/stocks/totals-by-code-barres
   */
  getTotalsByCodeBarres(
    codeBarres: string[],
    entrepotId?: number | null
  ): Observable<Record<string, number>> {
    const codes = [
      ...new Set((codeBarres || []).map((c) => this.cleanBarcode(c)).filter((c) => !!c)),
    ];
    if (!codes.length) {
      return of({});
    }
    return this.ensureSession().pipe(
      switchMap(() => {
        const body: Record<string, unknown> = { code_barres: codes };
        const eid = Number(entrepotId);
        if (Number.isFinite(eid) && eid > 0) {
          body.entrepot_id = eid;
        }
        const url = `${lcApiBase(this.authService)}/api/stocks/totals-by-code-barres`;
        return this.http
          .post<{ success?: boolean; data?: { totals?: Record<string, number> } }>(
            url,
            body,
            { headers: this.authHeaders() }
          )
          .pipe(
            map((res) => {
              const out: Record<string, number> = {};
              const raw = res?.data?.totals ?? {};
              for (const code of codes) {
                const n = Number(raw[code]);
                out[code] = Number.isFinite(n) ? n : 0;
              }
              return out;
            }),
            catchError(() => {
              const empty: Record<string, number> = {};
              for (const code of codes) {
                empty[code] = 0;
              }
              return of(empty);
            })
          );
      }),
      catchError(() => {
        const empty: Record<string, number> = {};
        for (const code of codes) {
          empty[code] = 0;
        }
        return of(empty);
      })
    );
  }

  private authHeaders(): HttpHeaders | undefined {
    const token = this.authService.getLogicielCaisseToken();
    if (!token?.trim()) {
      return undefined;
    }
    return new HttpHeaders({ Authorization: `Bearer ${token.trim()}` });
  }

  private cleanBarcode(value: unknown): string {
    const s = value != null ? String(value).replace(/\s/g, '').trim() : '';
    if (!s || s === '0' || s === '-' || s === '—') {
      return '';
    }
    return s;
  }

  private cleanRef(value: unknown): string {
    const s = value != null ? String(value).trim() : '';
    if (!s || s === '-' || s === '—' || s === 'null') {
      return '';
    }
    return s;
  }

  private ensureSession(): Observable<boolean> {
    if (this.authService.isAuthenticated() && this.authService.getBackStationId()) {
      return of(true);
    }
    return this.authService.bootstrap().pipe(
      map((status) => status === 'ready' && !!this.authService.getBackStationId()),
      switchMap((ok) =>
        ok
          ? of(true)
          : throwError(() => new Error('Connectez le module logiciel caisse pour alimenter le stock.'))
      ),
      catchError(() => throwError(() => new Error('Session logiciel caisse indisponible.')))
    );
  }

  /** Retour fournisseur — réserve stock_disponible (statut Crée). */
  retourFournisseurDisponibleSortie(params: {
    entrepot_id: number;
    reference_document?: string | null;
    lignes: LcBonReceptionEntreeLigne[];
  }): Observable<{ success: true; updated: number; already_applied?: boolean }> {
    return this.postRetourFournisseurStock(
      '/api/stocks/retour-fournisseur-disponible',
      'retour_fournisseur_disponible',
      params
    );
  }

  /** Retour fournisseur — sortie stock_physique (statut Retourné). */
  retourFournisseurPhysiqueSortie(params: {
    entrepot_id: number;
    reference_document?: string | null;
    lignes: LcBonReceptionEntreeLigne[];
  }): Observable<{ success: true; updated: number; already_applied?: boolean }> {
    return this.postRetourFournisseurStock(
      '/api/stocks/retour-fournisseur-physique',
      'retour_fournisseur_physique',
      params
    );
  }

  /** Annulation — réintègre stock_disponible réservé. */
  retourFournisseurAnnulerDisponible(params: {
    entrepot_id: number;
    reference_document?: string | null;
    lignes: LcBonReceptionEntreeLigne[];
  }): Observable<{ success: true; updated: number; already_applied?: boolean }> {
    return this.postRetourFournisseurStock(
      '/api/stocks/retour-fournisseur-annuler-disponible',
      'retour_fournisseur_annuler_disponible',
      params
    );
  }

  private postRetourFournisseurStock(
    path: string,
    erpMovement: string,
    params: {
      entrepot_id: number;
      reference_document?: string | null;
      lignes: LcBonReceptionEntreeLigne[];
    }
  ): Observable<{ success: true; updated: number; already_applied?: boolean }> {
    return this.ensureSession().pipe(
      switchMap(() => {
        const entrepotId = Number(params.entrepot_id);
        if (!Number.isFinite(entrepotId) || entrepotId <= 0) {
          return throwError(() => new Error('Entrepôt logiciel caisse invalide.'));
        }
        const lignes = (params.lignes || [])
          .map((l) => ({
            code_barre: this.cleanBarcode(l.code_barre),
            ref_interne: this.cleanRef(l.ref_interne),
            ref_fabricant: this.cleanRef(l.ref_fabricant),
            qnt: Math.abs(Number(l.qnt) || 0),
            libelle: l.libelle != null ? String(l.libelle).trim() || null : null,
            product_id: l.product_id,
          }))
          .filter(
            (l) =>
              Number(l.qnt) > 0 &&
              !!(l.code_barre || l.ref_interne || l.ref_fabricant || l.product_id)
          );
        if (!lignes.length) {
          return throwError(
            () => new Error('Aucune ligne exploitable pour le stock retour fournisseur.')
          );
        }
        const body = {
          entrepot_id: entrepotId,
          reference_document: params.reference_document || null,
          lignes,
        };
        type RetourStockRes = {
          success?: boolean;
          updated?: number;
          already_applied?: boolean;
          error?: string;
          message?: string;
        };
        const mapRetourStockResponse = (res: RetourStockRes) => {
          if (!res?.success) {
            throw new Error(res?.error || res?.message || 'Stock retour fournisseur refusé');
          }
          return {
            success: true as const,
            updated: Number(res.updated) || 0,
            already_applied: !!res.already_applied,
          };
        };

        const primaryUrl = `${lcApiBase(this.authService)}${path}`;
        const fallbackUrl = `${lcApiBase(this.authService)}/api/stocks/bon-sortie-sortie`;

        return this.http
          .post<RetourStockRes>(primaryUrl, body, { headers: this.authHeaders() })
          .pipe(
            map(mapRetourStockResponse),
            catchError((err: unknown) => {
              const isMissingRoute =
                err instanceof HttpErrorResponse &&
                (err.status === 404 || err.status === 405);
              if (!isMissingRoute) {
                return throwError(() =>
                  readObservableError(err, 'Stock retour fournisseur refusé.')
                );
              }
              return this.http
                .post<RetourStockRes>(
                  fallbackUrl,
                  { ...body, erp_movement: erpMovement },
                  { headers: this.authHeaders() }
                )
                .pipe(
                  map(mapRetourStockResponse),
                  catchError((fallbackErr: unknown) =>
                    throwError(() =>
                      readObservableError(
                        fallbackErr,
                        'Stock retour fournisseur refusé (mettre à jour le backend logiciel caisse).'
                      )
                    )
                  )
                );
            })
          );
      })
    );
  }
}
